/**
 * Application Configuration
 * Central location for constants, config values, and feature flags
 */

// ═══════════════════════════════════════════════════════════════════════════════
// ENVIRONMENT & API
// ═══════════════════════════════════════════════════════════════════════════════
export const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';
export const DEV_MODE = process.env.NODE_ENV !== 'production';

// ═══════════════════════════════════════════════════════════════════════════════
// LOCALSTORAGE KEYS
// ═══════════════════════════════════════════════════════════════════════════════
export const STORAGE_KEYS = {
  // Auth
  SESSION_TOKEN: 'session_token',
  USER_ID: 'user_id',
  
  // Guest mode
  GUEST_MODE_DATA: 'guest_mode_data',
  GUEST_ACTIVITY: 'guest_activity',
  
  // Migration
  MIQ_MIGRATION_FIRST_SESSION: 'miq_migration_first_session',
  
  // Nudges & Dismissals
  NUDGE_DISMISSED_PREFIX: 'nudge_dismissed_',
  COACH_DISMISSED_PREFIX: 'coach_dismissed_',
  POST_MEAL_SHEET_SHOWN: 'post_meal_sheet_last_shown',
  
  // Smart Timer
  IF_TIMER_START: 'if_timer_start',
  IF_TIMER_DURATION: 'if_timer_duration',
  
  // Favorites
  SMART_PICKS_FAVORITES: 'smartpicks_favorites_',
};

// ═══════════════════════════════════════════════════════════════════════════════
// TIMING CONSTANTS
// ═══════════════════════════════════════════════════════════════════════════════
export const TIMING = {
  // Post-meal sheet cooldown (2 hours)
  POST_MEAL_SHEET_COOLDOWN_MS: 2 * 60 * 60 * 1000,
  
  // Coach nudge dismiss duration (8 hours)
  COACH_DISMISS_DURATION_MS: 8 * 60 * 60 * 1000,
  
  // Toast durations
  TOAST_SUCCESS_DURATION: 4000,
  TOAST_ERROR_DURATION: 5000,
  TOAST_MIGRATION_DURATION: 5000,
};

// ═══════════════════════════════════════════════════════════════════════════════
// PREMIUM & MIQ COPY
// ═══════════════════════════════════════════════════════════════════════════════
export const MIQ_COPY = {
  FULL_NAME: 'MetaBolicIQ (MIQ) 🧠',
  SHORT_NAME: 'MIQ',
  PREMIUM_BADGE: '🟣 Premium Access',
  PREMIUM_TOOLTIP: 'These are advanced MetaBolicIQ (MIQ) 🧠 features. You currently have full access during your 30-day Intelligence Calibration Trial.',
  TRIAL_STATUS: 'Premium access active — Intelligence Calibration Trial',
  MIGRATION_TOAST: 'Your meals are now part of your MetaBolicIQ (MIQ) 🧠 — nothing lost.',
  MIGRATION_REASSURANCE: 'Your meals from before signing up continue to inform your MetaBolicIQ (MIQ).',
};

// ═══════════════════════════════════════════════════════════════════════════════
// KNOWN RESTAURANT CHAINS (for Smart Picks)
// ═══════════════════════════════════════════════════════════════════════════════
export const KNOWN_CHAINS = [
  // Fast Food - Global
  "mcdonald's", "mcdonalds", "burger king", "wendy's", "wendys", "kfc", "kentucky fried chicken",
  "subway", "taco bell", "pizza hut", "domino's", "dominos", "papa john's", "papa johns",
  "chick-fil-a", "chickfila", "popeyes", "five guys", "shake shack", "in-n-out", "in n out",
  "jack in the box", "carl's jr", "carls jr", "hardee's", "hardees", "arby's", "arbys",
  "sonic", "dairy queen", "dq", "whataburger", "white castle", "del taco", "el pollo loco",
  "wingstop", "buffalo wild wings", "bww", "raising cane's", "raising canes", "jersey mike's",
  "jimmy john's", "jimmy johns", "firehouse subs", "quiznos", "blaze pizza", "mod pizza",
  
  // Coffee & Breakfast - Global
  "starbucks", "dunkin", "dunkin donuts", "tim hortons", "peet's coffee", "peets",
  "caribou coffee", "the coffee bean", "coffee bean & tea leaf", "panera", "panera bread",
  "ihop", "denny's", "dennys", "waffle house", "cracker barrel", "bob evans",
  
  // Casual Dining - National
  "applebee's", "applebees", "chili's", "chilis", "olive garden", "red lobster",
  "outback steakhouse", "outback", "longhorn steakhouse", "longhorn", "texas roadhouse",
  "cheesecake factory", "the cheesecake factory", "buffalo wild wings", "tgi friday's",
  "tgi fridays", "red robin", "hooters", "twin peaks", "golden corral", "golden coral",
  "cheddar's", "cheddars", "cracker barrel", "bob evans", "perkins", "village inn",
  
  // Mexican - National
  "chipotle", "qdoba", "moe's", "moes", "moe's southwest", "baja fresh", "del taco",
  "taco cabana", "taco john's", "taco johns",
  
  // Asian - National
  "panda express", "p.f. chang's", "pf changs", "pei wei", "noodles & company",
  "wok box", "pick up stix", "yoshinoya", "teriyaki madness",
  
  // Italian - National
  "olive garden", "carrabba's", "carrabbas", "maggiano's", "maggianos", "buca di beppo",
  "fazoli's", "fazolis", "sbarro",
  
  // Seafood - National  
  "red lobster", "joe's crab shack", "bonefish grill", "captain d's", "long john silver's",
  
  // Pizza - National
  "pizza hut", "domino's", "dominos", "papa john's", "papa johns", "little caesars",
  "papa murphy's", "papa murphys", "marco's pizza", "marcos", "blaze pizza", "mod pizza",
  "cici's pizza", "cicis", "round table pizza", "mountain mike's",
  
  // Canadian Chains
  "tim hortons", "a&w", "mary brown's", "harvey's", "swiss chalet", "st-hubert",
  "boston pizza", "the keg", "montana's", "milestones", "cora", "mucho burrito",
  
  // UK/European Chains
  "nando's", "nandos", "greggs", "pret a manger", "pret", "wagamama", "pizza express",
  "frankie & benny's", "giraffe", "zizzi", "ask italian", "bella italia",
  
  // Australian Chains
  "hungry jack's", "hungry jacks", "grill'd", "nando's", "red rooster", "oporto",
  
  // Ice Cream & Desserts
  "baskin robbins", "baskin-robbins", "cold stone", "cold stone creamery", 
  "dairy queen", "dq", "ben & jerry's", "ben and jerry's", "haagen-dazs", "marble slab",
  "krispy kreme", "cinnabon", "auntie anne's", "auntie annes",
  
  // Smoothie & Health
  "jamba", "jamba juice", "smoothie king", "tropical smoothie", "robeks", "juice it up"
];

/**
 * Check if restaurant name matches a known national/global chain
 */
export const isKnownChainRestaurant = (query) => {
  if (!query || query.length < 3) return false;
  const normalizedQuery = query.toLowerCase().trim();
  return KNOWN_CHAINS.some(chain => 
    normalizedQuery.includes(chain) || chain.includes(normalizedQuery)
  );
};

// ═══════════════════════════════════════════════════════════════════════════════
// SMART SUGGESTIONS (for no-data fallback)
// ═══════════════════════════════════════════════════════════════════════════════
export const SMART_SUGGESTIONS = {
  burger: [
    { tip: "Remove top bun", impact: "~120 kcal saved", icon: "🍞" },
    { tip: "Skip the cheese", impact: "~100 kcal saved", icon: "🧀" },
    { tip: "Choose grilled over fried", impact: "~150 kcal saved", icon: "🔥" },
    { tip: "Ask for light sauce", impact: "~50-100 kcal saved", icon: "🥫" },
    { tip: "Swap fries for side salad", impact: "~250 kcal saved", icon: "🥗" }
  ],
  pizza: [
    { tip: "Choose thin crust", impact: "~100 kcal/slice saved", icon: "🍕" },
    { tip: "Go light on cheese", impact: "~80 kcal/slice saved", icon: "🧀" },
    { tip: "Load up on veggie toppings", impact: "More fiber, less fat", icon: "🥬" },
    { tip: "Avoid stuffed crust", impact: "~150 kcal/slice saved", icon: "🍞" },
    { tip: "Blot excess oil with napkin", impact: "~50 kcal/slice saved", icon: "🧻" }
  ],
  mexican: [
    { tip: "Choose corn over flour tortilla", impact: "~50 kcal saved", icon: "🌮" },
    { tip: "Skip the sour cream", impact: "~60 kcal saved", icon: "🥛" },
    { tip: "Ask for half the rice", impact: "~100 kcal saved", icon: "🍚" },
    { tip: "Load up on fajita veggies", impact: "More nutrients, fewer carbs", icon: "🫑" },
    { tip: "Choose pico over queso", impact: "~150 kcal saved", icon: "🍅" }
  ],
  asian: [
    { tip: "Choose steamed over fried rice", impact: "~200 kcal saved", icon: "🍚" },
    { tip: "Request sauce on the side", impact: "Control sodium & sugar", icon: "🥢" },
    { tip: "Go for grilled proteins", impact: "~100 kcal saved", icon: "🍗" },
    { tip: "Add extra vegetables", impact: "More fiber, less calories", icon: "🥦" },
    { tip: "Skip the crispy noodles", impact: "~150 kcal saved", icon: "🍜" }
  ],
  general: [
    { tip: "Ask for dressing on the side", impact: "Control portions", icon: "🥗" },
    { tip: "Choose grilled over fried", impact: "~150-200 kcal saved", icon: "🔥" },
    { tip: "Request half-portion if available", impact: "50% less everything", icon: "½" },
    { tip: "Skip the bread basket", impact: "~150-200 kcal saved", icon: "🥖" },
    { tip: "Choose water over sugary drinks", impact: "~150-300 kcal saved", icon: "💧" }
  ]
};

/**
 * Get cuisine type from query for smart suggestions
 */
export const getCuisineType = (query) => {
  if (!query) return 'general';
  const q = query.toLowerCase();
  if (q.includes('burger') || q.includes('fries') || q.includes('shake')) return 'burger';
  if (q.includes('pizza') || q.includes('calzone')) return 'pizza';
  if (q.includes('taco') || q.includes('burrito') || q.includes('quesadilla') || q.includes('mexican') || q.includes('chipotle')) return 'mexican';
  if (q.includes('chinese') || q.includes('sushi') || q.includes('thai') || q.includes('panda') || q.includes('noodle') || q.includes('rice') || q.includes('teriyaki')) return 'asian';
  return 'general';
};

// ═══════════════════════════════════════════════════════════════════════════════
// DEV-ONLY: FORCED FAILURE HARNESS
// ═══════════════════════════════════════════════════════════════════════════════

// Initialize the global forced-failure flag (dev only)
if (DEV_MODE && typeof window !== 'undefined') {
  // Only set if not already defined (allows persistence across hot reloads)
  if (window.__FORCE_MEAL_SAVE_FAIL__ === undefined) {
    window.__FORCE_MEAL_SAVE_FAIL__ = false;
  }
  
  // Helper function to check if forced failure is enabled
  window.__checkForcedFailure__ = () => {
    if (window.__FORCE_MEAL_SAVE_FAIL__) {
      console.warn('[DEV] Forced meal save failure triggered!');
      return true;
    }
    return false;
  };
  
  // Log availability
  console.log('[DEV] Forced failure harness available. Set window.__FORCE_MEAL_SAVE_FAIL__ = true to test failure paths.');
}

/**
 * Helper to simulate forced failure (returns true if should fail)
 * Only works in DEV mode
 */
export const shouldForceFailure = () => {
  if (!DEV_MODE) return false;
  return typeof window !== 'undefined' && window.__FORCE_MEAL_SAVE_FAIL__ === true;
};
